import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, getOrganizationSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function Insurance() {
  // SEO Configuration
  useSEO({
    title: 'Insurance Verification - The Villarreal Health Network',
    description: 'Verify your insurance coverage for home healthcare services. Instant verification for Medicare, Medicaid, and private insurance. Check benefits, copays, and authorization requirements.',
    keywords: 'insurance verification, Medicare coverage, Medicaid benefits, home health insurance, healthcare coverage verification Las Vegas',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "name": "Insurance Coverage Verification",
          "description": "Verify insurance coverage for home healthcare services",
          "provider": getOrganizationSchema()
        },
        {
          "@type": "Service",
          "name": "Insurance Coverage Verification",
          "serviceType": "Insurance Verification",
          "description": "Instant verification of Medicare, Medicaid, and private insurance coverage for home healthcare services in Las Vegas",
          "provider": getOrganizationSchema(),
          "areaServed": { "@type": "State", "name": "Nevada" }
        },
        getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Insurance Verification', url: '/insurance' }
        ])
      ]
    }
  });

  const [formData, setFormData] = useState({
    patientName: '',
    dateOfBirth: '',
    insuranceType: '',
    insuranceProvider: '',
    memberId: '',
    groupNumber: '',
    policyHolder: '',
    relationshipToPatient: '',
    servicesNeeded: [],
    hasSecondaryInsurance: false,
    secondaryProvider: '',
    secondaryMemberId: ''
  });

  const [verificationResult, setVerificationResult] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const insuranceTypes = [
    'Medicare',
    'Medicaid',
    'Medicaid Waiver',
    'Private Insurance',
    'Employer Insurance',
    'ACA Marketplace',
    'TRICARE',
    'VA Benefits',
    'Private Pay'
  ];

  const servicesNeeded = [
    'Skilled Nursing',
    'Physical Therapy',
    'Occupational Therapy',
    'Speech Therapy',
    'Personal Care',
    'Companion Care',
    'Medical Equipment',
    'Home Modifications'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox' && name === 'servicesNeeded') {
      setFormData(prev => ({
        ...prev,
        servicesNeeded: checked 
          ? [...prev.servicesNeeded, value]
          : prev.servicesNeeded.filter(item => item !== value)
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    setVerificationResult(null);

    // Simulate verification process
    setTimeout(() => {
      const mockResult = {
        status: 'verified',
        coverage: {
          skilledNursing: true,
          physicalTherapy: true,
          occupationalTherapy: true,
          speechTherapy: false,
          personalCare: formData.insuranceType === 'Medicaid Waiver',
          companionCare: formData.insuranceType === 'Medicaid Waiver',
          medicalEquipment: true,
          homeModifications: formData.insuranceType === 'Medicaid Waiver'
        },
        copay: formData.insuranceType === 'Medicare' ? '$0' : 'Varies',
        deductible: formData.insuranceType === 'Medicare' ? 'Met' : '$500 remaining',
        authorizationRequired: formData.insuranceType !== 'Medicare',
        effectiveDate: '2024-01-01',
        expirationDate: '2024-12-31'
      };
      setVerificationResult(mockResult);
      setIsVerifying(false);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-600 via-teal-600 to-green-700 text-white py-24 pt-36 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/55950c5465e285f24af1158f1aa6ab56.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-8">
              <span className="inline-block bg-white/20 text-white px-8 py-4 rounded-full text-xl font-semibold backdrop-blur-sm shadow-lg">
                ✓ Insurance Coverage Verification
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-8">
              Check Your Coverage Options
            </h1>
            <p className="text-2xl text-green-100 max-w-4xl mx-auto leading-relaxed">
              Verify your insurance coverage for home healthcare services instantly. Get detailed information about benefits, copays, and authorization requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Coverage Benefits Overview */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Insurance Types We Accept</h2>
            <p className="text-xl text-gray-600">We work with all major insurance providers</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-3xl shadow-xl p-10 text-center border border-gray-100">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                <i className="ri-government-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Medicare</h3>
              <ul className="text-gray-600 space-y-3 text-lg">
                <li>✓ Skilled nursing care</li>
                <li>✓ Physical therapy</li>
                <li>✓ Occupational therapy</li>
                <li>✓ Medical equipment</li>
                <li>✓ No copay for qualified services</li>
              </ul>
            </div>

            <div className="bg-white rounded-3xl shadow-xl p-10 text-center border border-gray-100">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                <i className="ri-shield-check-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Medicaid Waiver</h3>
              <ul className="text-gray-600 space-y-3 text-lg">
                <li>✓ Personal care services</li>
                <li>✓ Companion care</li>
                <li>✓ Home modifications</li>
                <li>✓ Respite care</li>
                <li>✓ Extended coverage options</li>
              </ul>
            </div>

            <div className="bg-white rounded-3xl shadow-xl p-10 text-center border border-gray-100">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                <i className="ri-building-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Private Insurance</h3>
              <ul className="text-gray-600 space-y-3 text-lg">
                <li>✓ Varies by plan</li>
                <li>✓ Prior authorization may be required</li>
                <li>✓ Copays and deductibles apply</li>
                <li>✓ We verify benefits for you</li>
                <li>✓ Direct billing available</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Verification Form */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Verify Your Coverage
            </h2>
            <p className="text-xl text-gray-600">
              Complete the form below for instant coverage verification
            </p>
          </div>

          <form onSubmit={handleVerification} className="bg-white rounded-3xl shadow-2xl p-12 border border-gray-100">
            {/* Patient Information */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                <i className="ri-user-line mr-4 text-green-600"></i>
                Patient Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Patient Name *
                  </label>
                  <input
                    type="text"
                    name="patientName"
                    value={formData.patientName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
                    placeholder="Patient's full name"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
                  />
                </div>
              </div>
            </div>

            {/* Insurance Information */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                <i className="ri-shield-check-line mr-4 text-green-600"></i>
                Primary Insurance Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Insurance Type *
                  </label>
                  <select
                    name="insuranceType"
                    value={formData.insuranceType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg pr-8"
                  >
                    <option value="">Select insurance type</option>
                    {insuranceTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Insurance Provider *
                  </label>
                  <input
                    type="text"
                    name="insuranceProvider"
                    value={formData.insuranceProvider}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
                    placeholder="e.g., Aetna, Blue Cross, Humana"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Member ID *
                  </label>
                  <input
                    type="text"
                    name="memberId"
                    value={formData.memberId}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
                    placeholder="Member ID number"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Group Number
                  </label>
                  <input
                    type="text"
                    name="groupNumber"
                    value={formData.groupNumber}
                    onChange={handleInputChange}
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
                    placeholder="Group number (if applicable)"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Policy Holder Name
                  </label>
                  <input
                    type="text"
                    name="policyHolder"
                    value={formData.policyHolder}
                    onChange={handleInputChange}
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
                    placeholder="If different from patient"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Relationship to Patient
                  </label>
                  <select
                    name="relationshipToPatient"
                    value={formData.relationshipToPatient}
                    onChange={handleInputChange}
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg pr-8"
                  >
                    <option value="">Select relationship</option>
                    <option value="self">Self</option>
                    <option value="spouse">Spouse</option>
                    <option value="child">Child</option>
                    <option value="parent">Parent</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Services Needed */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                <i className="ri-service-line mr-4 text-green-600"></i>
                Services Needed
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {servicesNeeded.map((service) => (
                  <label key={service} className="flex items-center cursor-pointer p-4 border-2 border-gray-200 rounded-xl hover:bg-green-50 hover:border-green-300">
                    <input
                      type="checkbox"
                      name="servicesNeeded"
                      value={service}
                      checked={formData.servicesNeeded.includes(service)}
                      onChange={handleInputChange}
                      className="mr-4 h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="text-gray-700 font-medium">{service}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Secondary Insurance */}
            <div className="mb-12">
              <div className="flex items-center mb-6">
                <input
                  type="checkbox"
                  name="hasSecondaryInsurance"
                  checked={formData.hasSecondaryInsurance}
                  onChange={handleInputChange}
                  className="mr-4 h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label className="text-xl font-bold text-gray-900">
                  I have secondary insurance
                </label>
              </div>

              {formData.hasSecondaryInsurance && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block font-bold text-gray-700 mb-4 text-lg">
                      Secondary Provider
                    </label>
                    <input
                      type="text"
                      name="secondaryProvider"
                      value={formData.secondaryProvider}
                      onChange={handleInputChange}
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
                      placeholder="Secondary insurance provider"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 mb-4 text-lg">
                      Secondary Member ID
                    </label>
                    <input
                      type="text"
                      name="secondaryMemberId"
                      value={formData.secondaryMemberId}
                      onChange={handleInputChange}
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
                      placeholder="Secondary member ID"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <Button
                type="submit"
                size="lg"
                disabled={isVerifying}
                className="w-full md:w-auto bg-green-600 hover:bg-green-700 whitespace-nowrap text-xl px-12 py-5 shadow-xl"
              >
                {isVerifying ? (
                  <>
                    <i className="ri-loader-4-line mr-3 animate-spin"></i>
                    Verifying Coverage...
                  </>
                ) : (
                  <>
                    <i className="ri-shield-check-line mr-3"></i>
                    Verify Coverage
                  </>
                )}
              </Button>
            </div>
          </form>

          {/* Verification Results */}
          {verificationResult && (
            <div className="mt-12 bg-green-50 border-2 border-green-200 rounded-3xl p-12">
              <div className="text-center mb-10">
                <i className="ri-check-circle-line text-green-600 text-6xl mb-6"></i>
                <h3 className="text-3xl font-bold text-green-800 mb-4">Coverage Verified!</h3>
                <p className="text-green-700 text-xl">Your insurance coverage has been successfully verified</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h4 className="text-2xl font-bold text-gray-900 mb-6">Coverage Details</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Copay:</span>
                      <span className="font-semibold text-gray-900">{verificationResult.copay}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Deductible:</span>
                      <span className="font-semibold text-gray-900">{verificationResult.deductible}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Authorization Required:</span>
                      <span className="font-semibold text-gray-900">
                        {verificationResult.authorizationRequired ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Coverage Period:</span>
                      <span className="font-semibold text-gray-900">
                        {verificationResult.effectiveDate} - {verificationResult.expirationDate}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h4 className="text-2xl font-bold text-gray-900 mb-6">Covered Services</h4>
                  <div className="space-y-3">
                    {Object.entries(verificationResult.coverage).map(([service, covered]) => (
                      <div key={service} className="flex items-center justify-between">
                        <span className="text-gray-700 capitalize">
                          {service.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        {covered ? (
                          <i className="ri-check-line text-green-600 text-xl"></i>
                        ) : (
                          <i className="ri-close-line text-red-500 text-xl"></i>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="text-center mt-10">
                <p className="text-green-700 text-lg mb-6">
                  Ready to start your care? Our team will handle all authorization requirements.
                </p>
                <Button
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 whitespace-nowrap text-xl px-12 py-4"
                  onClick={() => window.location.href = '/referral'}
                >
                  <i className="ri-file-text-line mr-3"></i>
                  Start Your Referral
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}